/** Block-level snap points so page clips do not cut through a line of text. */

const SNAP_SELECTORS = [
  'h1',
  'h2',
  'h3',
  'p',
  'li',
  'ul',
  'table',
  'tr',
  '.mb-4',
  '.mb-6',
  '[data-role-id]',
  '[data-certification-id]',
  '[data-education-id]',
  '[data-project-id]',
  '.paysafe-right-block',
].join(',')

const MIN_SLICE_PX = 24

/** Wrapped bullet copy — line rects must come from the text span, not the flex `li` box. */
const BULLET_TEXT_LINE_SELECTORS = 'li > span.flex-1, li > .paysafe-preview-bullet-text'

/**
 * Atomic preview blocks — keep on one page when possible.
 * Right-column edu/certs/skills flow line-by-line in PDF (checkPageBreakColumn per line).
 */
const KEEP_TOGETHER_BLOCK_SELECTORS = [
  '.paysafe-preview-bullet',
  '.paysafe-skill-block',
  '[data-role-id]',
  '[data-certification-id]',
  '[data-education-id]',
  '[data-project-id]',
].join(',')

/** Line carriers inside right-column cards (not the outer wrapper — it spans multiple lines as one rect). */
const RIGHT_COLUMN_LINE_SELECTORS =
  '.paysafe-right-block > div, .paysafe-right-block > a, .paysafe-right-block > h3'

/** First visible line carriers inside a column (incl. skill line wraps). */
const COLUMN_SLICE_LINE_SELECTORS = [
  'h2',
  'h3',
  'p',
  'li',
  BULLET_TEXT_LINE_SELECTORS,
  '.paysafe-skill-block h3',
  '.paysafe-skill-block .paysafe-skill-body',
  '.paysafe-skill-block .paysafe-skill-token',
  '.paysafe-skill-block .text-sm',
  RIGHT_COLUMN_LINE_SELECTORS,
  '[data-role-id]',
  '[data-certification-id]',
  '[data-education-id]',
  '[data-project-id]',
].join(',')

export type KeepTogetherBlock = { top: number; bottom: number }

function isBulletTextSpan(el: HTMLElement): boolean {
  const parent = el.parentElement
  return (
    parent?.tagName === 'LI' &&
    (el.classList.contains('flex-1') || el.classList.contains('paysafe-preview-bullet-text'))
  )
}

function isRightColumnLineCarrier(el: HTMLElement): boolean {
  return Boolean(el.closest('.paysafe-skill-block') || el.closest('.paysafe-right-block'))
}

function isBulletMarkerSpan(el: HTMLElement): boolean {
  const parent = el.parentElement
  return parent?.tagName === 'LI' && el.tagName === 'SPAN' && !isBulletTextSpan(el)
}

function shouldSkipLeadLineSnap(el: Element): boolean {
  if (!(el instanceof HTMLElement)) return true
  if (isBulletMarkerSpan(el)) return true
  if (isBulletTextSpan(el)) return false
  const parent = el.parentElement
  if (parent?.tagName === 'LI' && el.tagName === 'SPAN') return true
  if (el.closest('.paysafe-skill-block') || el.closest('.paysafe-right-block')) {
    return false
  }
  return shouldSkipNestedSnap(el)
}

export type ResumePreviewSliceOptions = {
  /** Printable height on sheet page 1 when a header sits above the column (two-column). */
  firstZoneHeightPx?: number
  /** Skill/cert/role blocks that must not split across pages unless taller than one zone. */
  keepTogetherBlocks?: KeepTogetherBlock[]
}

export function collectKeepTogetherBlockRanges(root: HTMLElement): KeepTogetherBlock[] {
  const rootTop = root.getBoundingClientRect().top
  const blocks: KeepTogetherBlock[] = []
  root.querySelectorAll(KEEP_TOGETHER_BLOCK_SELECTORS).forEach((el) => {
    if (!(el instanceof HTMLElement)) return
    const rect = el.getBoundingClientRect()
    if (rect.height < 1) return
    blocks.push({
      top: Math.round(rect.top - rootTop),
      bottom: Math.round(rect.bottom - rootTop),
    })
  })
  return blocks.sort((a, b) => a.top - b.top)
}

function isKeepTogetherBlockElement(el: Element): boolean {
  return el instanceof HTMLElement && el.matches(KEEP_TOGETHER_BLOCK_SELECTORS)
}

/** Line-level snaps — right-column cards and skills always use per-line rects. */
function shouldAddLineRectSnaps(el: HTMLElement): boolean {
  if (isBulletTextSpan(el)) return true
  if (isRightColumnLineCarrier(el)) return true
  const keeper = el.closest(KEEP_TOGETHER_BLOCK_SELECTORS)
  if (!keeper) return true
  if (isKeepTogetherBlockElement(el)) return false
  if (keeper instanceof HTMLElement && keeper.classList.contains('paysafe-preview-bullet')) {
    return isBulletTextSpan(el)
  }
  return keeper instanceof HTMLElement && keeper.hasAttribute('data-role-id')
}

export type CollectSnapOffsetsOptions = {
  /**
   * For table cells: ignore stretched cell height (row = max(left, right)).
   * Snap only to block bottoms inside the column.
   */
  contentOnly?: boolean
}

function addLineRectSnaps(el: HTMLElement, rootTop: number, offsets: Set<number>) {
  const style = window.getComputedStyle(el)
  if (style.display === 'none' || style.visibility === 'hidden') return
  const rects = el.getClientRects()
  for (let i = 0; i < rects.length; i++) {
    const r = rects[i]
    if (!r || r.height < 0.5) continue
    const top = Math.round(r.top - rootTop)
    const bottom = Math.round(r.bottom - rootTop)
    if (top > 0) offsets.add(top)
    if (bottom > 0) offsets.add(bottom)
  }
}

export function collectResumePreviewSnapOffsets(
  root: HTMLElement,
  options?: CollectSnapOffsetsOptions
): number[] {
  const offsets = new Set<number>([0])
  const rootTop = root.getBoundingClientRect().top

  const addBottom = (el: Element) => {
    if (!(el instanceof HTMLElement)) return
    const rect = el.getBoundingClientRect()
    if (rect.height < 1) return
    const y = Math.round(rect.bottom - rootTop)
    if (y > 0) offsets.add(y)
  }

  if (!options?.contentOnly) {
    addBottom(root)
  }
  root.querySelectorAll(SNAP_SELECTORS).forEach((el) => {
    if (shouldSkipNestedSnap(el)) return
    addBottom(el)
  })

  const lineSelectors = options?.contentOnly ? COLUMN_SLICE_LINE_SELECTORS : SNAP_SELECTORS
  root.querySelectorAll(lineSelectors).forEach((el) => {
    if (!(el instanceof HTMLElement)) return
    if (options?.contentOnly) {
      if (isBulletMarkerSpan(el)) return
      if (el.tagName === 'LI' && el.querySelector(BULLET_TEXT_LINE_SELECTORS)) return
    } else if (shouldSkipNestedSnap(el)) {
      return
    }
    if (!el.textContent?.trim()) return
    if (!shouldAddLineRectSnaps(el)) return
    addLineRectSnaps(el, rootTop, offsets)
  })

  root.querySelectorAll(BULLET_TEXT_LINE_SELECTORS).forEach((el) => {
    if (!(el instanceof HTMLElement)) return
    if (!el.textContent?.trim()) return
    addLineRectSnaps(el, rootTop, offsets)
  })

  root.querySelectorAll(KEEP_TOGETHER_BLOCK_SELECTORS).forEach((el) => {
    if (!(el instanceof HTMLElement)) return
    const rect = el.getBoundingClientRect()
    if (rect.height < 1) return
    const top = Math.round(rect.top - rootTop)
    const bottom = Math.round(rect.bottom - rootTop)
    if (top > 0) offsets.add(top)
    if (bottom > 0) offsets.add(bottom)
  })

  if (!options?.contentOnly) {
    const total = Math.round(root.scrollHeight)
    offsets.add(total)
  } else if (offsets.size === 1) {
    offsets.add(Math.round(root.scrollHeight))
  }

  return Array.from(offsets).sort((a, b) => a - b)
}

/** Use outer blocks only — inner headings/lines must not create a second break inside the block. */
function shouldSkipNestedSnap(el: Element): boolean {
  const parent = el.parentElement
  if (!parent) return false
  if (parent.tagName === 'LI' && el.tagName === 'SPAN') return true
  if (el.closest('.paysafe-right-block') && !el.matches(RIGHT_COLUMN_LINE_SELECTORS)) {
    return true
  }
  return false
}

function blockSpansSlice(block: KeepTogetherBlock, start: number): boolean {
  return block.bottom > start + MIN_SLICE_PX && block.top < block.bottom
}

function isValidKeepTogetherBreak(
  y: number,
  start: number,
  zone: number,
  blocks: KeepTogetherBlock[]
): boolean {
  for (const block of blocks) {
    if (y <= block.top || y >= block.bottom) continue
    if (block.top < start) {
      return block.bottom - block.top > zone
    }
    return false
  }
  return true
}

/**
 * If the proposed break splits a keep-together block, end the page before the block
 * or after the full block when it still fits on the sheet.
 */
export function adjustBreakForKeepTogetherBlocks(
  proposed: number,
  start: number,
  idealEnd: number,
  zone: number,
  blocks: KeepTogetherBlock[]
): number {
  let next = proposed
  for (const block of blocks) {
    if (!blockSpansSlice(block, start)) continue
    if (block.top >= idealEnd) continue

    const blockHeight = block.bottom - block.top
    const splitsBlock = next > block.top && next < block.bottom
    const endsInsideBlock = next >= block.top && next < block.bottom

    if (block.top >= start - 1 && block.top < idealEnd) {
      if (block.bottom <= idealEnd) {
        if (splitsBlock) next = block.bottom
        continue
      }
      if (endsInsideBlock || idealEnd < block.bottom) {
        if (block.top > start + MIN_SLICE_PX) {
          next = Math.min(next, block.top)
        } else if (blockHeight > zone) {
          continue
        } else {
          next = block.top
        }
      }
    }
  }
  return next
}

function pickNextSliceStart(
  start: number,
  zone: number,
  totalHeightPx: number,
  snapOffsetsPx: number[],
  keepTogether: KeepTogetherBlock[]
): number | null {
  if (start >= totalHeightPx - 2) return null
  if (start + zone >= totalHeightPx - 2) return null

  const idealEnd = Math.min(start + zone, totalHeightPx)
  const minEnd = start + MIN_SLICE_PX

  const validInZone = snapOffsetsPx.filter(
    (s) =>
      s >= minEnd &&
      s <= idealEnd &&
      s > start &&
      isValidKeepTogetherBreak(s, start, zone, keepTogether)
  )

  let nextStart: number

  if (validInZone.length > 0) {
    nextStart = validInZone[validInZone.length - 1]!
  } else {
    const nextBoundary = snapOffsetsPx.find(
      (s) => s > minEnd && isValidKeepTogetherBreak(s, start, zone, keepTogether)
    )
    if (nextBoundary == null) return null

    if (nextBoundary <= idealEnd) {
      nextStart = nextBoundary
    } else {
      const beforeBoundary = snapOffsetsPx.filter(
        (s) =>
          s > start &&
          s < nextBoundary &&
          s <= idealEnd &&
          isValidKeepTogetherBreak(s, start, zone, keepTogether)
      )
      if (beforeBoundary.length > 0) {
        nextStart = beforeBoundary[beforeBoundary.length - 1]!
      } else {
        const blockAtBoundary = keepTogether.find((b) => b.top === nextBoundary)
        if (blockAtBoundary && blockAtBoundary.top > start + MIN_SLICE_PX) {
          nextStart = blockAtBoundary.top
        } else {
          return null
        }
      }
    }
  }

  nextStart = adjustBreakForKeepTogetherBlocks(
    nextStart,
    start,
    idealEnd,
    zone,
    keepTogether
  )

  if (nextStart - start > zone) {
    const strict = snapOffsetsPx.filter(
      (s) =>
        s > start + MIN_SLICE_PX &&
        s <= start + zone &&
        isValidKeepTogetherBreak(s, start, zone, keepTogether)
    )
    if (strict.length > 0) {
      nextStart = strict[strict.length - 1]!
    } else {
      const fallback = snapOffsetsPx.find(
        (s) => s > start + MIN_SLICE_PX && s - start <= zone
      )
      if (fallback == null) return null
      nextStart = fallback
    }
  }

  if (nextStart <= start) return null
  return nextStart
}

/**
 * Start offsets for each page slice (first always 0).
 * Breaks only on snap points — never at an arbitrary pixel inside a line.
 */
export function buildResumePreviewSliceStarts(
  totalHeightPx: number,
  zoneHeightPx: number,
  snapOffsetsPx: number[],
  options?: ResumePreviewSliceOptions
): number[] {
  if (zoneHeightPx <= 0 || totalHeightPx <= 0) return [0]

  const firstZone =
    options?.firstZoneHeightPx != null && options.firstZoneHeightPx > 0
      ? Math.min(options.firstZoneHeightPx, zoneHeightPx)
      : zoneHeightPx
  const keepTogether = options?.keepTogetherBlocks ?? []

  const starts = [0]
  let start = 0
  let pageIndex = 0

  while (true) {
    const zone = pageIndex === 0 ? firstZone : zoneHeightPx
    const nextStart = pickNextSliceStart(
      start,
      zone,
      totalHeightPx,
      snapOffsetsPx,
      keepTogether
    )
    if (nextStart == null) break
    starts.push(nextStart)
    start = nextStart
    pageIndex += 1
  }

  return starts
}

type LineRectCallback = (top: number, bottom: number) => void

/** Every rendered text line in a column (bullet wraps included). */
function forEachColumnLineRect(colEl: HTMLElement, fn: LineRectCallback): void {
  const rootTop = colEl.getBoundingClientRect().top

  const emitElementLines = (el: HTMLElement) => {
    if (!el.textContent?.trim()) return
    const rects = el.getClientRects()
    for (let i = 0; i < rects.length; i++) {
      const r = rects[i]
      if (!r || r.height < 0.5) continue
      fn(Math.round(r.top - rootTop), Math.round(r.bottom - rootTop))
    }
  }

  colEl.querySelectorAll('.paysafe-skill-block').forEach((block) => {
    if (!(block instanceof HTMLElement)) return
    const title = block.querySelector('h3')
    if (title instanceof HTMLElement) emitElementLines(title)
    const tokens = block.querySelectorAll('.paysafe-skill-token')
    if (tokens.length > 0) {
      tokens.forEach((token) => {
        if (token instanceof HTMLElement) emitElementLines(token)
      })
      return
    }
    const body = block.querySelector('.paysafe-skill-body, .text-sm')
    if (body instanceof HTMLElement) emitElementLines(body)
  })

  colEl.querySelectorAll(BULLET_TEXT_LINE_SELECTORS).forEach((el) => {
    if (el instanceof HTMLElement) emitElementLines(el)
  })

  colEl.querySelectorAll(COLUMN_SLICE_LINE_SELECTORS).forEach((el) => {
    if (!(el instanceof HTMLElement)) return
    if (el.closest('.paysafe-skill-block')) return
    if (shouldSkipLeadLineSnap(el)) return
    if (isBulletTextSpan(el)) return
    if (el.tagName === 'LI' && el.querySelector(BULLET_TEXT_LINE_SELECTORS)) return
    if (!el.textContent?.trim()) return
    emitElementLines(el)
  })
}

/** If offset cuts through a line, move the break to that line's top (whole line on next page). */
export function alignSliceBreakOffset(colEl: HTMLElement, offset: number): number {
  if (offset <= 0) return 0
  let aligned = offset
  forEachColumnLineRect(colEl, (top, bottom) => {
    if (top < offset + 1 && bottom > offset + 1) {
      aligned = Math.min(aligned, top)
    }
  })
  return aligned
}

function measureColumnTotalHeightPx(colEl: HTMLElement): number {
  let maxBottom = Math.round(colEl.scrollHeight)
  forEachColumnLineRect(colEl, (_top, bottom) => {
    if (bottom > maxBottom) maxBottom = bottom
  })
  return maxBottom
}

/** End offset for one column page: last full line bottom in zone, or line top if zone cuts mid-line. */
function clampColumnPageEndToZone(
  colEl: HTMLElement,
  end: number,
  viewTop: number,
  idealEnd: number
): number {
  if (end <= idealEnd + 0.5) return end

  let straddleTop: number | null = null
  forEachColumnLineRect(colEl, (top, bottom) => {
    if (top < idealEnd + 1 && bottom > idealEnd + 1) {
      straddleTop = straddleTop == null ? top : Math.min(straddleTop, top)
    }
  })
  if (straddleTop != null && straddleTop > viewTop + MIN_SLICE_PX) {
    return alignSliceBreakOffset(colEl, straddleTop)
  }

  let lastInsideBottom: number | null = null
  forEachColumnLineRect(colEl, (top, bottom) => {
    if (bottom <= idealEnd + 0.5 && bottom > viewTop + MIN_SLICE_PX) {
      lastInsideBottom = lastInsideBottom == null ? bottom : Math.max(lastInsideBottom, bottom)
    }
  })
  if (lastInsideBottom != null) return lastInsideBottom
  return idealEnd
}

function snapColumnPageEndOffset(
  colEl: HTMLElement,
  sliceStart: number,
  leadPx: number,
  zonePx: number,
  blocks: KeepTogetherBlock[],
  totalHeight: number
): number | null {
  const viewTop = sliceStart + leadPx
  if (viewTop >= totalHeight - 2) return null

  const idealEnd = viewTop + zonePx
  let end = idealEnd
  let lastLineBottom: number | null = null
  let splitLineTop: number | null = null

  forEachColumnLineRect(colEl, (top, bottom) => {
    if (bottom <= viewTop + 0.5) return
    if (top < idealEnd + 1 && bottom > idealEnd + 1) {
      splitLineTop = splitLineTop == null ? top : Math.min(splitLineTop, top)
    }
    if (bottom <= idealEnd + 0.5 && bottom > viewTop + MIN_SLICE_PX) {
      lastLineBottom =
        lastLineBottom == null ? bottom : Math.max(lastLineBottom, bottom)
    }
  })

  if (splitLineTop != null && splitLineTop > viewTop + MIN_SLICE_PX) {
    end = splitLineTop
  } else if (lastLineBottom != null && lastLineBottom > viewTop + MIN_SLICE_PX) {
    end = lastLineBottom
  }

  end = adjustBreakForKeepTogetherBlocks(end, sliceStart, idealEnd, zonePx, blocks)
  end = alignSliceBreakOffset(colEl, end)
  end = clampColumnPageEndToZone(colEl, end, viewTop, idealEnd)

  if (end <= sliceStart + MIN_SLICE_PX) {
    let firstBottom: number | null = null
    forEachColumnLineRect(colEl, (_top, bottom) => {
      if (bottom > viewTop + MIN_SLICE_PX) {
        firstBottom = firstBottom == null ? bottom : Math.min(firstBottom, bottom)
      }
    })
    if (firstBottom == null) return null
    end = firstBottom
  }

  if (end >= totalHeight - 2) return null
  return Math.round(end)
}

/** DOM line rects — primary pagination for column preview (matches on-screen column layout). */
/** Max visible band height (px) for one column page slice — for tests and clip height. */
export function columnSliceBandHeightPx(
  starts: number[],
  leads: number[],
  pageIndex: number,
  totalHeightPx?: number
): number {
  if (!columnHasPreviewPage(starts, pageIndex)) return 0
  const start = starts[pageIndex]!
  const lead = leads[pageIndex] ?? 0
  const next = starts[pageIndex + 1]
  if (next == null) {
    if (totalHeightPx == null || totalHeightPx <= 0) return 0
    return Math.max(0, totalHeightPx - start - lead)
  }
  return Math.max(0, next - start - lead)
}

export function buildColumnSliceStartsFromDom(
  colEl: HTMLElement,
  zoneHeightPx: number,
  firstPageZonePx?: number
): number[] {
  const keepTogether = collectKeepTogetherBlockRanges(colEl)
  const totalHeight = measureColumnTotalHeightPx(colEl)
  const starts = [0]
  let start = 0
  let pageIndex = 0

  while (true) {
    const zone =
      pageIndex === 0 && firstPageZonePx != null && firstPageZonePx > 0
        ? Math.min(firstPageZonePx, zoneHeightPx)
        : zoneHeightPx
    const lead = measureColumnSliceLeadPx(colEl, start)
    const viewTop = start + lead
    if (viewTop >= totalHeight - 2) break
    if (viewTop + zone >= totalHeight - 2) break

    const end = snapColumnPageEndOffset(
      colEl,
      start,
      lead,
      zone,
      keepTogether,
      totalHeight
    )
    if (end == null || end <= start + MIN_SLICE_PX) break

    const alignedEnd = alignSliceBreakOffset(colEl, end)
    if (alignedEnd <= start + MIN_SLICE_PX) break

    const bandPx = alignedEnd - viewTop
    if (bandPx > zone + 1) break

    starts.push(alignedEnd)
    start = alignedEnd
    pageIndex += 1
  }

  return starts
}

export type ColumnSliceStateResult = {
  starts: number[]
  leads: number[]
  totalHeight: number
}

export function buildColumnSliceState(
  colEl: HTMLElement | null,
  zoneHeightPx: number,
  firstPageZonePx?: number
): ColumnSliceStateResult {
  if (!colEl || zoneHeightPx <= 0) {
    return { starts: [0], leads: [0], totalHeight: 0 }
  }

  const totalHeight = measureColumnTotalHeightPx(colEl)
  const starts = buildColumnSliceStartsFromDom(colEl, zoneHeightPx, firstPageZonePx)
  const leads = buildColumnSliceLeads(colEl, starts)

  return { starts, leads, totalHeight }
}

/**
 * Pixels from sliceStart to the first line top to show (may be negative when break landed mid-line).
 */
export function measureColumnSliceLeadPx(
  colEl: HTMLElement,
  sliceStartPx: number
): number {
  if (sliceStartPx <= 0) return 0
  let containingTop: number | null = null
  let minRelTop: number | null = null

  forEachColumnLineRect(colEl, (top, bottom) => {
    if (top < sliceStartPx + 1 && bottom > sliceStartPx + 1) {
      containingTop = containingTop == null ? top : Math.min(containingTop, top)
    }
    if (top >= sliceStartPx - 1) {
      minRelTop = minRelTop == null ? top : Math.min(minRelTop, top)
    }
  })

  if (containingTop != null) {
    return containingTop - sliceStartPx
  }
  if (minRelTop == null) return 0
  return minRelTop - sliceStartPx
}

export function buildColumnSliceLeads(
  colEl: HTMLElement,
  starts: number[]
): number[] {
  return starts.map((start) => measureColumnSliceLeadPx(colEl, start))
}

export function columnHasPreviewPage(starts: number[], pageIndex: number): boolean {
  return pageIndex >= 0 && pageIndex < starts.length
}

export function sliceHeightForPage(
  starts: number[],
  pageIndex: number,
  totalHeightPx: number
): number {
  if (!columnHasPreviewPage(starts, pageIndex)) return 0
  const start = starts[pageIndex]!
  const next = starts[pageIndex + 1] ?? totalHeightPx
  return Math.max(0, next - start)
}

/** Printable column band height on a sheet (page 1 may reserve header band above columns). */
export function columnSheetBandHeightPx(
  pageIndex: number,
  contentZoneHeightPx: number,
  headerHeightPx: number
): number {
  if (contentZoneHeightPx <= 0) return 0
  if (pageIndex === 0 && headerHeightPx > 0) {
    return Math.max(0, contentZoneHeightPx - headerHeightPx)
  }
  return contentZoneHeightPx
}

/** Max clip height for a column slice on a given sheet (page 1 may reserve header band). */
export function columnClipHeightForSheet(
  pageIndex: number,
  sliceHeightPx: number,
  contentZoneHeightPx: number,
  headerHeightPx: number
): number {
  const maxBand = columnSheetBandHeightPx(pageIndex, contentZoneHeightPx, headerHeightPx)
  if (sliceHeightPx <= 0) return 0
  return Math.min(sliceHeightPx, maxBand)
}

/**
 * Two-column preview: both columns on the same sheet share the full printable band
 * so the page edge (bottom clip) aligns horizontally across left and right.
 */
export function twoColumnColumnViewportHeightPx(
  pageIndex: number,
  hasColumnPage: boolean,
  contentZoneHeightPx: number,
  headerHeightPx: number
): number {
  if (!hasColumnPage) return 0
  return columnSheetBandHeightPx(pageIndex, contentZoneHeightPx, headerHeightPx)
}
